/**
 * ADR-0048 phase 2 — what PRODUCTION says about an item training authored.
 *
 * Phase 1 gave training a promote button and stamped `_provenance` on the
 * document it wrote into production. Nothing read that stamp back, so from
 * production's side a promoted item was indistinguishable from one a
 * librarian wrote here — which is the thing ADR-0048 §3 said a librarian
 * needs to be able to tell.
 *
 * This module is the DECISIONS, kept pure so they can be tested without a
 * browser: what the chip says, what the filter matches, and who may withdraw
 * a promotion. The rendering lives in tree.ts and detail.ts; the withdrawal
 * is enforced for real by functions/lib/catalogue_withdrawal.py — this file
 * only decides what to OFFER, exactly as promote-rules.ts does.
 */
import type { AccessRole } from "../admin/claims";

/** The stamp catalogue_promote writes. Every field is optional on read: this
 *  is data from another environment, and a half-written document should
 *  degrade to "no chip" rather than throw in a list render. */
export interface PromotionProvenance {
  origin?: string;
  sourceDatabase?: string;
  sourceDocId?: string;
  sourceCollection?: string;
  promotedBy?: {
    email?: string;
    trainingUid?: string;
    trainingPersonDocId?: string;
    role?: string;
  };
  promotedAt?: string;
  promotionCount?: number;
  lastPromotionWasUpdate?: boolean;
  /** Written by a withdrawal; see withdrawnNotice below. */
  withdrawnAt?: string;
  withdrawnBy?: { email?: string; personDocId?: string };
}

/** Mirrors PROVENANCE_ORIGIN in functions/lib/catalogue_promotion.py. */
export const TRAINING_ORIGIN = "training";

/**
 * Was this item authored in training and adopted here?
 *
 * Both halves matter. `origin` alone is not enough: ADR-0046 uses the same
 * `_provenance.origin` key on PEOPLE for "onetouch"/"stc"/"guest", and a
 * future author could stamp origin without ever promoting. Requiring a
 * promotion count keeps the chip tied to an act that actually happened.
 */
export function isPromotedFromTraining(
  prov: PromotionProvenance | null | undefined,
): boolean {
  if (!prov || prov.origin !== TRAINING_ORIGIN) return false;
  return (prov.promotionCount ?? 0) >= 1;
}

export interface ChipModel {
  /** Short word in the pill. */
  label: string;
  /** The `title=` tooltip — the whole story, for the librarian who hovers. */
  title: string;
}

/**
 * The chip a promoted item wears in production, or null for everything else.
 *
 * Deliberately quiet. ADR-0046's read side says provenance is "information,
 * not controls", and a promoted item is not a problem — it is the system
 * working. So this is a neutral `.scope-badge`, the same pill the person
 * rows use for "OneTouch", and NOT an alarm colour.
 */
export function trainingChip(
  prov: PromotionProvenance | null | undefined,
): ChipModel | null {
  if (!isPromotedFromTraining(prov)) return null;
  const who = prov?.promotedBy?.email?.trim();
  const when = (prov?.promotedAt ?? "").slice(0, 10);
  const count = prov?.promotionCount ?? 1;

  const parts = ["Authored in training"];
  if (who) parts.push(`promoted by ${who}`);
  if (when) parts.push(when);
  if (count > 1) parts.push(`${count} promotions`);

  return { label: "Training", title: parts.join(" · ") };
}

/**
 * A sentence for the detail panel, where there is room for one.
 *
 * Says the same thing as the chip's tooltip, but written to be read rather
 * than hovered — and it names the environment explicitly, because "training"
 * inside a production planner is otherwise a word without a context.
 */
export function provenanceSentence(
  prov: PromotionProvenance | null | undefined,
): string | null {
  if (!isPromotedFromTraining(prov)) return null;
  const who = prov?.promotedBy?.email?.trim();
  const when = (prov?.promotedAt ?? "").slice(0, 10);
  const count = prov?.promotionCount ?? 1;

  let s = "This item was authored in the training environment";
  if (who) s += ` and promoted to production by ${who}`;
  if (when) s += ` on ${when}`;
  s += ".";
  if (count > 1) {
    s += ` It has been promoted ${count} times — the latest promotion replaced the previous version.`;
  }
  return s;
}

/** If a promotion has been withdrawn, say so — otherwise null. */
export function withdrawnNotice(
  prov: PromotionProvenance | null | undefined,
): string | null {
  const at = prov?.withdrawnAt;
  if (!at) return null;
  const who = prov?.withdrawnBy?.email?.trim();
  const when = at.slice(0, 10);
  return who
    ? `Withdrawn from production by ${who} on ${when}. The training author can promote it again.`
    : `Withdrawn from production on ${when}. The training author can promote it again.`;
}

// ── the filter ──────────────────────────────────────────────────────────

/** Toolbar lens. `all` is the default; nothing is hidden unless asked. */
export type ProvenanceFilter = "all" | "training" | "local";

export function matchesProvenanceFilter(
  prov: PromotionProvenance | null | undefined,
  filter: ProvenanceFilter,
): boolean {
  if (filter === "all") return true;
  const promoted = isPromotedFromTraining(prov);
  return filter === "training" ? promoted : !promoted;
}

/** Empty-state wording, so the list can say WHICH lens emptied it. */
export function provenanceFilterEmptyText(filter: ProvenanceFilter): string | null {
  if (filter === "training") {
    return "No items in this view were authored in training.";
  }
  if (filter === "local") {
    return "Every item in this view was authored in training.";
  }
  return null;
}

// ── withdrawal (the correction path) ────────────────────────────────────

/**
 * Ed, 2026-09-18: "for a company as small as ours, if we got the promotion
 * from an admin/practice-author wrong we'd delete from production and let
 * them try again."
 *
 * So there is no approval queue and no diff review — just a way to take a
 * wrong promotion back out. Two bounds make that safe to offer:
 *
 *   1. ONLY promoted items. This is not a general delete door for the
 *      production catalogue; an item production authored itself is not
 *      withdrawable here, and the server refuses it independently.
 *   2. ONLY in production. Withdrawing in training would mean nothing —
 *      the promoted copy lives here.
 */
export const WITHDRAW_ROLES: ReadonlyArray<string> = ["admin", "librarian"];
export const WITHDRAW_DATABASE = "(default)";

export function canWithdraw(
  databaseId: string,
  role: AccessRole | string | null | undefined,
  prov: PromotionProvenance | null | undefined,
): boolean {
  if (databaseId !== WITHDRAW_DATABASE) return false;
  if (!WITHDRAW_ROLES.includes(String(role ?? ""))) return false;
  return isPromotedFromTraining(prov);
}

/**
 * What the confirm dialog says.
 *
 * ☠️ It must NOT say "this cannot be undone" — the existing catalogue delete
 * modal says that and it is false there too (delete.ts writes
 * `{status:"retired", active:false}`, a soft retire). Here it would be
 * doubly false: the whole point is that the author promotes again.
 */
export function withdrawConfirmText(
  title: string,
  prov: PromotionProvenance | null | undefined,
): string {
  const who = prov?.promotedBy?.email?.trim();
  const from = who ? ` ${who}` : " the author";
  return (
    `Withdraw “${title}” from the production catalogue?\n\n` +
    `It stops appearing in plans built here. It is NOT deleted — the item ` +
    `stays in training, and${from} can correct it and promote it again.`
  );
}
